import { useCallback, useEffect, useRef, useState } from "react";
import Image from "@tiptap/extension-image";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import { Plugin } from "@tiptap/pm/state";

const ImageCaptionView = ({ node, updateAttributes, selected }) => {
  const { src, alt, caption, width, textAlign } = node.attrs;
  const containerRef = useRef(null);
  const [isResizing, setIsResizing] = useState(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const onMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    startX.current = e.clientX;
    startWidth.current = containerRef.current?.querySelector("img")?.offsetWidth || 300;
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const onMouseMove = (e) => {
      const diff = e.clientX - startX.current;
      const newWidth = Math.max(100, startWidth.current + diff);
      updateAttributes({ width: newWidth });
    };

    const onMouseUp = () => setIsResizing(false);

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [isResizing, updateAttributes]);

  const alignStyle = {
    left: { marginLeft: 0, marginRight: "auto" },
    center: { marginLeft: "auto", marginRight: "auto" },
    right: { marginLeft: "auto", marginRight: 0 },
  };

  return (
    <NodeViewWrapper>
      <figure
        ref={containerRef}
        style={{
          ...alignStyle[textAlign || "center"],
          width: width ? `${width}px` : "auto",
          maxWidth: "100%",
        }}
        className="my-4 not-prose"
      >
        {/* Image + resize handle */}
        <div className="relative inline-block w-full">
          <img
            src={src}
            alt={alt || caption || ""}
            draggable={false}
            className={`block w-full h-auto rounded-lg ${
              selected
                ? "ring-2 ring-blue-500 ring-offset-2"
                : ""
            }`}
          />
          {/* Resize handle – bottom-right corner */}
          {selected && (
            <div
              onMouseDown={onMouseDown}
              className="absolute -bottom-1.5 -right-1.5 w-3.5 h-3.5 bg-blue-600 border-2 border-white rounded-sm cursor-nwse-resize shadow"
            />
          )}
        </div>

        {/* Caption input */}
        <input
          type="text"
          value={caption || ""}
          onChange={(e) => updateAttributes({ caption: e.target.value })}
          placeholder="Add a caption…"
          className="block w-full mt-2 px-1 py-0.5 text-sm italic text-gray-500 text-center bg-transparent border-none outline-none placeholder:text-gray-300 focus:text-gray-700"
        />
      </figure>
    </NodeViewWrapper>
  );
};

const ImageCaptionExtension = Image.extend({
  name: "image",

  addAttributes() {
    return {
      ...this.parent?.(),
      caption: { default: null },
      width: { default: null },
      textAlign: { default: "center" },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImageCaptionView);
  },

  renderHTML({ HTMLAttributes }) {
    const { caption, width, textAlign, ...imgAttrs } = HTMLAttributes;

    const figureStyle = [
      `text-align: ${textAlign || "center"}`,
      width ? `width: ${width}px; max-width: 100%` : "max-width: 100%",
      textAlign === "left"
        ? "margin-left: 0; margin-right: auto"
        : textAlign === "right"
          ? "margin-left: auto; margin-right: 0"
          : "margin-left: auto; margin-right: auto",
    ].join("; ");

    if (caption) {
      return [
        "figure",
        { style: figureStyle },
        ["img", { ...imgAttrs, style: "width: 100%; height: auto" }],
        ["figcaption", { style: "font-style: italic" }, caption],
      ];
    }

    return [
      "figure",
      { style: figureStyle },
      ["img", { ...imgAttrs, style: "width: 100%; height: auto" }],
    ];
  },

  addProseMirrorPlugins() {
    const insertImage = (file, view, pos) => {
      if (!file.type.startsWith("image/")) return false;

      const reader = new FileReader();
      reader.onload = () => {
        const { schema } = view.state;
        const node = schema.nodes.image.create({ src: reader.result });
        const tr = view.state.tr.insert(pos, node);
        view.dispatch(tr);
      };
      reader.readAsDataURL(file);
      return true;
    };

    return [
      new Plugin({
        props: {
          handleDrop(view, event) {
            const files = event.dataTransfer?.files;
            if (!files || files.length === 0) return false;

            const images = Array.from(files).filter((f) =>
              f.type.startsWith("image/")
            );
            if (images.length === 0) return false;

            event.preventDefault();
            const pos = view.posAtCoords({
              left: event.clientX,
              top: event.clientY,
            })?.pos;
            if (pos == null) return false;

            images.forEach((file) => insertImage(file, view, pos));
            return true;
          },
          handlePaste(view, event) {
            const items = event.clipboardData?.items;
            if (!items) return false;

            const images = Array.from(items)
              .filter((item) => item.type.startsWith("image/"))
              .map((item) => item.getAsFile())
              .filter(Boolean);
            if (images.length === 0) return false;

            event.preventDefault();
            const pos = view.state.selection.from;
            images.forEach((file) => insertImage(file, view, pos));
            return true;
          },
        },
      }),
    ];
  },

  parseHTML() {
    return [
      {
        tag: "figure",
        getAttrs(dom) {
          const img = dom.querySelector("img");
          if (!img) return false;
          const figcaption = dom.querySelector("figcaption");
          const style = dom.getAttribute("style") || "";
          const widthMatch = style.match(/width:\s*(\d+)px/);
          let textAlign = "center";
          if (style.includes("margin-right: auto") && style.includes("margin-left: 0")) textAlign = "left";
          else if (style.includes("margin-left: auto") && style.includes("margin-right: 0")) textAlign = "right";

          return {
            src: img.getAttribute("src"),
            alt: img.getAttribute("alt"),
            title: img.getAttribute("title"),
            caption: figcaption?.textContent || null,
            width: widthMatch ? parseInt(widthMatch[1], 10) : null,
            textAlign,
          };
        },
      },
      {
        tag: "img[src]",
      },
    ];
  },
});

export default ImageCaptionExtension;
